import type { StateCreator, StoreMutatorIdentifier } from "zustand"
import type { MethodState } from "./types"

/**
 * Zustand middleware type helpers for TypeScript
 */
type Middleware = <
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  f: StateCreator<T, Mps, Mcs>
) => StateCreator<T, Mps, Mcs>

/**
 * Creates a composed middleware stack for Zustand stores
 *
 * This function is a type-safe helper for composing multiple Zustand middlewares.
 * Use this in generated stores to ensure proper typing.
 *
 * @example
 * ```ts
 * import { create } from 'zustand'
 * import { subscribeWithSelector, persist, devtools } from 'zustand/middleware'
 * import { composeMiddleware } from '@fermata/grpc-zustand/runtime'
 *
 * const useStore = create(
 *   composeMiddleware(
 *     devtools,
 *     persist,
 *     subscribeWithSelector
 *   )((set) => ({
 *     // state here
 *   }))
 * )
 * ```
 */
export function composeMiddleware<T>(
  ...middlewares: Middleware[]
): (f: StateCreator<T>) => StateCreator<T> {
  return (f: StateCreator<T>) => {
    return middlewares.reduceRight((acc, middleware) => middleware(acc), f)
  }
}

/**
 * Helper to create initial method state
 */
export function createMethodState<T = unknown>(): MethodState<T> {
  return {
    data: undefined,
    loading: false,
    error: undefined,
    byKey: {},
  }
}

/**
 * Stable serialization of a request, used to key cached results per request
 * shape. Inline object literals (e.g. `useFoo({})`) serialize identically so
 * they share a cache entry instead of looping. Object keys are sorted so two
 * equivalent requests assembled in a different insertion order map to the same
 * cache entry. Handles BigInt (int64/uint64) fields that the native
 * `JSON.stringify` cannot serialize. Requests that are `undefined`/`null` map
 * to a shared default key.
 */
export function requestKey(req: unknown): string {
  if (req === undefined || req === null) return "__noreq__"
  return JSON.stringify(canonicalize(req)) ?? "__noreq__"
}

/** Recursively sort object keys and stringify BigInt for a stable cache key. */
function canonicalize(value: unknown): unknown {
  if (typeof value === "bigint") return value.toString()
  if (Array.isArray(value)) return value.map(canonicalize)
  if (value && typeof value === "object") {
    const sorted: Record<string, unknown> = {}
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      sorted[key] = canonicalize((value as Record<string, unknown>)[key])
    }
    return sorted
  }
  return value
}

/**
 * Type guard to check if error is a gRPC error
 */
export function isGrpcError(
  error: unknown
): error is Error & { code?: string; metadata?: Record<string, string> } {
  return error instanceof Error && "code" in error
}

/**
 * Format gRPC error for display
 */
function decodeGrpcMessage(raw: string): string {
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

export function formatGrpcError(error: unknown): string {
  if (!error) return "Unknown error"

  if (isGrpcError(error)) {
    return `[${error.code || "UNKNOWN"}] ${decodeGrpcMessage(error.message)}`
  }

  if (error instanceof Error) {
    return decodeGrpcMessage(error.message)
  }

  return String(error)
}

/**
 * Storage adapter for protobuf messages in localStorage
 * Handles serialization/deserialization of protobuf types
 */
export interface ProtoStorageAdapter {
  getItem: (name: string) => string | null | Promise<string | null>
  setItem: (name: string, value: string) => void | Promise<void>
  removeItem: (name: string) => void | Promise<void>
}

/**
 * Create a storage adapter that properly handles protobuf message serialization
 */
export function createProtoStorage(): ProtoStorageAdapter {
  return {
    getItem: (name: string) => {
      const value = localStorage.getItem(name)
      return value
    },
    setItem: (name: string, value: string) => {
      localStorage.setItem(name, value)
    },
    removeItem: (name: string) => {
      localStorage.removeItem(name)
    },
  }
}

/**
 * Serialize protobuf message for storage
 * Converts protobuf objects to plain JSON, handling BigInt values
 */
export function serializeProtoState(state: unknown): string {
  const serializable: Record<string, unknown> = {}

  if (!state || typeof state !== "object") {
    return JSON.stringify(state, bigIntReplacer)
  }

  for (const [key, value] of Object.entries(state)) {
    if (typeof value === "function") {
      // Skip functions
      continue
    }

    if (value && typeof value === "object" && "data" in value) {
      // Handle MethodState objects
      serializable[key] = {
        data: value.data ? convertBigIntToString(value.data) : undefined,
        loading: false, // Don't persist loading state
        error: undefined, // Don't persist errors
      }
    } else {
      serializable[key] = value
    }
  }

  return JSON.stringify(serializable, bigIntReplacer)
}

/**
 * JSON replacer function to convert BigInt to string
 */
function bigIntReplacer(_key: string, value: unknown): unknown {
  if (typeof value === "bigint") {
    return value.toString()
  }
  return value
}

/**
 * Recursively convert BigInt values to strings in an object
 */
function convertBigIntToString(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (typeof obj === "bigint") {
    return obj.toString()
  }

  if (Array.isArray(obj)) {
    return obj.map(convertBigIntToString)
  }

  if (typeof obj === "object") {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertBigIntToString(value)
    }
    return result
  }

  return obj
}

/**
 * Deserialize protobuf state from storage
 */
export function deserializeProtoState(str: string): unknown {
  try {
    return JSON.parse(str)
  } catch {
    return {}
  }
}
